/**
 * StagePill — one stage as a pill ("3 Quote Sent") on the pipeline row or in
 * Hidden Stages, in the plan progress bar's colours (selected, move-to,
 * default). Every pill but the selected stage's drags by a small grip, so a
 * tap on the label still opens its settings (PillSettingsPopover) and a phone
 * can still scroll past it. The selected pill is fixed in place. On a Locked
 * page (plan 2026-10-10-001, U7) no pill drags or shows a grip; its settings
 * still open, to read.
 */
import { useDraggable } from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { cn } from '@/lib/utils';
import { pillDragId } from './pillDnd';
import { PillSettingsPopover } from './PillSettingsPopover';
import { PILL_TONE_CLASS, pillLabel, pillTone, type PillModel } from './stagePipelineModel';

const PILL_CLASS = 'inline-flex h-9 items-center rounded-full text-xs font-medium text-white shadow-sm';

export interface StagePillProps {
  pill: PillModel;
  /** In Hidden Stages: "move to" only, no arrow. */
  hidden?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (patch: { moveTo?: boolean; showArrow?: boolean }) => void;
  className?: string;
}

export function StagePill({ pill, hidden = false, open, onOpenChange, onChange, className }: StagePillProps) {
  const { stage, rule, selected } = pill;
  const tone = pillTone(pill);
  const locked = useAdminPageLevel() === 'locked';
  const canDrag = !selected && !locked;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: pillDragId(stage.id),
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      data-testid="pill"
      data-stage-id={stage.id}
      data-stage-name={stage.name}
      data-tone={tone}
      className={cn(PILL_CLASS, PILL_TONE_CLASS[tone], tone === 'move-to' && 'hover:bg-[#555]', isDragging && 'opacity-40', className)}
    >
      {canDrag && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="flex h-full shrink-0 cursor-grab touch-none items-center rounded-l-full pl-2 pr-0.5 text-white/70 hover:text-white active:cursor-grabbing"
          aria-label={`Drag ${stage.name}`}
          title={hidden ? 'Drag onto the pipeline to show it' : 'Drag onto … to hide it'}
        >
          <GripVertical className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      <PillSettingsPopover
        stageId={stage.id}
        stageName={stage.name}
        open={open}
        onOpenChange={onOpenChange}
        offerMoveTo={!selected}
        offerShowArrow={!hidden}
        moveTo={rule.moveTo}
        showArrow={rule.showArrow}
        onChange={onChange}
      >
        <button
          type="button"
          className={cn(
            'h-full min-w-0 flex-1 truncate whitespace-nowrap rounded-full pr-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
            canDrag ? 'pl-1' : 'pl-3.5',
          )}
        >
          {pillLabel(stage)}
        </button>
      </PillSettingsPopover>
    </div>
  );
}

/** The pill under the pointer while it is dragged (DragOverlay). */
export function StagePillFace({ pill }: { pill: PillModel }) {
  return (
    <div className={cn(PILL_CLASS, PILL_TONE_CLASS[pillTone(pill)], 'cursor-grabbing px-3.5 shadow-md')}>
      <GripVertical className="-ml-1.5 mr-1 h-3.5 w-3.5 text-white/70" aria-hidden />
      {pillLabel(pill.stage)}
    </div>
  );
}
